import { useState } from 'react';
import {
  Box, Flex, Heading, Text,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import Card from '../../components/card/Card';
import DataTable from '../../components/table/DataTable';
import TableSearch from '../../components/table/TableSearch';

interface AuditLogEntry {
  id: string;
  adminUserId: string;
  adminEmail: string;
  action: string;
  entityType: string;
  entityId: string;
  organizationId: string | null;
  details: string | null;
  ipAddress: string | null;
  createdAt: string;
}

interface AuditLogPage {
  items: AuditLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const columnHelper = createColumnHelper<AuditLogEntry>();

export function AuditLogPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryTextColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const modalBg = useColorModeValue('gray.50', 'navy.900');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit', page, actionFilter, entityTypeFilter],
    queryFn: () => apiClient.get<AuditLogPage>('/admin/audit', {
      page: page + 1,
      pageSize,
      ...(actionFilter && { action: actionFilter }),
      ...(entityTypeFilter && { entityType: entityTypeFilter }),
    }),
  });

  const columns = [
    columnHelper.accessor('createdAt', {
      header: t('admin.audit.timestamp'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor}>
          {new Date(info.getValue()).toLocaleString()}
        </Text>
      ),
    }),
    columnHelper.accessor('adminEmail', {
      header: t('admin.audit.admin'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('action', {
      header: t('admin.audit.action'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('entityType', {
      header: t('admin.audit.entityType'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('entityId', {
      header: t('admin.audit.entityId'),
      cell: (info) => (
        <Text fontSize="sm" color={secondaryTextColor} fontFamily="mono">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('details', {
      header: t('admin.audit.details'),
      cell: (info) => (
        <Text fontSize="sm" color={secondaryTextColor} noOfLines={1} maxW="200px">
          {info.getValue() ?? '-'}
        </Text>
      ),
    }),
  ];

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="audit-log-page">
      <Card p="20px" mb="20px">
        <Heading as="h1" size="lg" color={textColor} mb="20px">
          {t('admin.audit.title')}
        </Heading>

        <TableSearch
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(0); }}
          searchTestId="search-input"
          filters={[
            {
              key: 'action',
              label: t('admin.audit.action'),
              options: [
                { value: '', label: t('common.all', 'All') },
              ],
              value: actionFilter,
              onChange: (value) => { setActionFilter(value); setPage(0); },
              testId: 'filter-action',
            },
            {
              key: 'entityType',
              label: t('admin.audit.entityType'),
              options: [
                { value: '', label: t('common.all', 'All') },
              ],
              value: entityTypeFilter,
              onChange: (value) => { setEntityTypeFilter(value); setPage(0); },
              testId: 'filter-entity-type',
            },
          ]}
        />
      </Card>

      <DataTable<AuditLogEntry>
        data={data?.items ?? []}
        columns={columns as ColumnDef<AuditLogEntry>[]}
        totalCount={data?.totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        isLoading={isLoading}
        onRowClick={setSelectedEntry}
        emptyText={t('admin.common.noResults')}
        data-testid="audit-log-table"
      />

      {/* Detail Modal */}
      <Modal isOpen={!!selectedEntry} onClose={() => setSelectedEntry(null)} size="lg">
        <ModalOverlay />
        <ModalContent data-testid="audit-detail-modal">
          <ModalHeader color={textColor}>{t('admin.audit.details')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedEntry && (
              <Flex direction="column" gap={3}>
                <Flex gap={4} flexWrap="wrap">
                  <Box>
                    <Text color={secondaryTextColor}>{t('admin.audit.admin')}</Text>
                    <Text color={textColor}>{selectedEntry.adminEmail}</Text>
                  </Box>
                  <Box>
                    <Text color={secondaryTextColor}>{t('admin.audit.action')}</Text>
                    <Text color={textColor}>{selectedEntry.action}</Text>
                  </Box>
                  <Box>
                    <Text color={secondaryTextColor}>{t('admin.audit.entityType')}</Text>
                    <Text color={textColor}>{selectedEntry.entityType}</Text>
                  </Box>
                  <Box>
                    <Text color={secondaryTextColor}>{t('admin.audit.entityId')}</Text>
                    <Text color={textColor}>{selectedEntry.entityId}</Text>
                  </Box>
                </Flex>
                {selectedEntry.organizationId && (
                  <Box>
                    <Text color={secondaryTextColor}>Organization ID</Text>
                    <Text color={textColor}>{selectedEntry.organizationId}</Text>
                  </Box>
                )}
                {selectedEntry.ipAddress && (
                  <Box>
                    <Text color={secondaryTextColor}>IP Address</Text>
                    <Text color={textColor}>{selectedEntry.ipAddress}</Text>
                  </Box>
                )}
                {selectedEntry.details && (
                  <Box p={3} bg={modalBg} borderRadius="md">
                    <Text fontFamily="mono" whiteSpace="pre-wrap" color={textColor}>
                      {selectedEntry.details}
                    </Text>
                  </Box>
                )}
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
